const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user_data';

// Flag to prevent multiple redirects
let isLoggingOut = false;

// Force logout function - immediate redirect
const forceLogout = (reason) => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  
  if (reason) {
    sessionStorage.setItem('logout_reason', reason);
  }
  
  // Immediate redirect - no delays
  window.location.replace('/login');
};

export const authService = {
  login: async (username, password) => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Invalid credentials');
    }
    
    const data = await response.json();
    isLoggingOut = false; // Reset flag on successful login
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify({
      username: data.username,
      role: data.role,
      features: data.features || {},
      company_code: data.company_code
    }));
    return data;
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  forceLogout: forceLogout,

  getToken: () => {
    return localStorage.getItem(TOKEN_KEY);
  },

  getUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  isAdmin: () => {
    const user = authService.getUser();
    return user?.role === 'admin';
  },

  // Check if session is still valid - call this periodically
  checkSession: async () => {
    const token = authService.getToken();
    if (!token) return false;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        const error = await response.json().catch(() => ({}));
        forceLogout(error.detail || 'Your access has been revoked');
        return false;
      }
      
      if (response.status === 401) {
        forceLogout('Session expired');
        return false;
      }
      
      return response.ok;
    } catch {
      return false;
    }
  }
};

export const apiRequest = async (url, options = {}) => {
  // Don't make requests if already logging out
  if (isLoggingOut) {
    throw new Error('Session ended');
  }
  
  const token = authService.getToken();
  
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
    ...options,
    headers,
  });
  
  // Handle unauthorized (token expired/invalid)
  if (response.status === 401) {
    forceLogout('Session expired');
    throw new Error('Unauthorized');
  }
  
  // Handle forbidden (user/passkey/tenant disabled) - IMMEDIATE logout
  if (response.status === 403) {
    const error = await response.json().catch(() => ({ detail: 'Access denied' }));
    // Force logout immediately for any 403 from protected endpoints
    forceLogout(error.detail || 'Access denied');
    throw new Error(error.detail || 'Access denied');
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  if (response.headers.get('content-type')?.includes('text/csv')) {
    return response.blob();
  }
  
  return response.json();
};